import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  makeStyles,
  Button,
  Chip,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  CircularProgress,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { green } from "@material-ui/core/colors";
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  DeleteOutline,
} from "@material-ui/icons";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ChatbotModal from "../ChatbotModal";
import MessageBubblePreview from "../MessageBubblePreview";
import {
  buildMessageVariables,
  fetchContactCustomFieldNames,
  fetchWasheetMessageVariables,
} from "../../utils/messageVariables";

const NO_SEQUENCE = "no-sequence";
const sequenceSelectMenuProps = {
  PaperProps: {
    style: {
      maxHeight: 320,
    },
  },
};

const useStyles = makeStyles((theme) => ({
  root: {
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    width: "100%",
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  textQuickAnswerContainer: {
    width: "100%",
  },
  formControl: {
    width: "100%",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 180px 220px 220px",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  block: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 6,
    background: "#fafafa",
  },
  blockHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  blockActions: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  blockGrid: {
    display: "grid",
    gridTemplateColumns: "330px minmax(0, 1fr) 90px",
    gap: theme.spacing(1),
    alignItems: "start",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "290px minmax(0, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  messageDelayField: {
    [theme.breakpoints.down("sm")]: {
      gridColumn: "2",
    },
    [theme.breakpoints.down("xs")]: {
      gridColumn: "auto",
    },
  },
  messageTextWide: {
    gridColumn: "2 / 4",
    [theme.breakpoints.down("xs")]: {
      gridColumn: "auto",
    },
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  fileName: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: theme.palette.text.secondary,
  },
  helper: {
    display: "block",
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  variablesBox: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 6,
    background: "#fff",
  },
  variablesLabel: {
    display: "block",
    marginBottom: theme.spacing(0.75),
    color: theme.palette.text.secondary,
  },
  variablesChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
  },
}));

const acceptedMedia =
  "image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.zip,.rar,.webp";

const createBlock = () => ({
  text: "",
  delaySeconds: 0,
  file: null,
  media: null,
});

const formatSequenceOption = sequence =>
  `ID: ${sequence.id} - ${sequence.name}`;

const normalizeConnectionIds = (...values) =>
  Array.from(
    new Set(
      values
        .flat()
        .map(Number)
        .filter(Boolean)
    )
  );

const sequenceMatchesConnections = (sequence, connectionIds) => {
  if (!connectionIds.length) return true;

  const sequenceConnectionIds = normalizeConnectionIds(
    sequence.connectionId,
    sequence.sharedConnectionIds || []
  );

  return sequenceConnectionIds.some(connectionId =>
    connectionIds.includes(connectionId)
  );
};

const QuickAnswerSchema = Yup.object().shape({
  shortcut: Yup.string()
    .min(2, "Muy corto")
    .max(15, "Muy largo")
    .required("Requerido"),
});

const QuickAnswersModal = ({
  open,
  onClose,
  quickAnswerId,
  initialValues,
  onSave,
  defaultConnectionId,
  categories = [],
}) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const initialState = {
    shortcut: "",
    category: "General",
    message: "",
    actionType: "message",
    chatbotSequenceId: "",
    connectionId: "",
    sharedConnectionIds: [],
    editBeforeSend: false,
  };

  const [quickAnswer, setQuickAnswer] = useState(initialState);
  const [blocks, setBlocks] = useState([createBlock()]);
  const [sequences, setSequences] = useState([]);
  const [connections, setConnections] = useState([]);
  const [customFieldNames, setCustomFieldNames] = useState([]);
  const [washeetVariables, setWasheetVariables] = useState([]);
  const [sequenceModalOpen, setSequenceModalOpen] = useState(false);
  const [sequenceModalId, setSequenceModalId] = useState(null);
  const [sequenceModalConnectionId, setSequenceModalConnectionId] = useState("");
  const sequenceModalApplySelection = useRef(null);
  const messageVariables = buildMessageVariables(customFieldNames, washeetVariables);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchQuickAnswer = async () => {
      if (initialValues) {
        setQuickAnswer((prevState) => {
          return { ...prevState, ...initialValues };
        });
      }

      try {
        const [
          { data: sequenceData },
          { data: connectionData },
          nextCustomFieldNames,
          nextWasheetVariables,
        ] = await Promise.all([
          api.get("/chatbot/sequences", {
            params: {
              all: true,
              pageNumber: 1,
              searchParam: "",
            },
          }),
          api.get("/connections/"),
          fetchContactCustomFieldNames(),
          fetchWasheetMessageVariables(),
        ]);
        if (isMounted.current) {
          setSequences(sequenceData.sequences || []);
          setConnections(connectionData || []);
          setCustomFieldNames(nextCustomFieldNames);
          setWasheetVariables(nextWasheetVariables);
        }
      } catch (err) {
        toastError(err);
      }

      if (!quickAnswerId) {
        setQuickAnswer({
          ...initialState,
          connectionId: defaultConnectionId || "",
          sharedConnectionIds: defaultConnectionId ? [Number(defaultConnectionId)] : [],
        });
        setBlocks([createBlock()]);
        return;
      }

      try {
        const { data } = await api.get(`/quickAnswers/${quickAnswerId}`);
        if (isMounted.current) {
          setQuickAnswer({
            ...data,
            category: data.category || "General",
            actionType: data.actionType || "message",
            chatbotSequenceId: data.chatbotSequenceId || "",
            connectionId: data.connectionId || "",
            sharedConnectionIds: data.sharedConnectionIds?.length
              ? data.sharedConnectionIds.map(Number)
              : data.connectionId
              ? [Number(data.connectionId)]
              : [],
            editBeforeSend: data.actionType !== "sequence" && !data.blocks?.length,
          });
          setBlocks(
            data.blocks?.length
              ? data.blocks.map(block => ({
                  text: block.text || "",
                  delaySeconds: Number(block.delaySeconds || 0),
                  file: null,
                  media: block.media || null,
                }))
              : [
                  {
                    ...createBlock(),
                    text: data.message || "",
                  },
                ]
          );
        }
      } catch (err) {
        toastError(err);
      }
    };

    if (open) {
      fetchQuickAnswer();
    }
  }, [quickAnswerId, open, initialValues, defaultConnectionId]);

  const handleClose = () => {
    onClose();
    setQuickAnswer(initialState);
    setBlocks([createBlock()]);
  };

  const updateBlock = (index, patch) => {
    setBlocks(prevBlocks =>
      prevBlocks.map((block, blockIndex) =>
        blockIndex === index ? { ...block, ...patch } : block
      )
    );
  };

  const moveBlock = (index, direction) => {
    setBlocks(prevBlocks => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= prevBlocks.length) return prevBlocks;

      const nextBlocks = [...prevBlocks];
      const current = nextBlocks[index];
      nextBlocks[index] = nextBlocks[nextIndex];
      nextBlocks[nextIndex] = current;
      return nextBlocks;
    });
  };

  const removeBlock = index => {
    setBlocks(prevBlocks =>
      prevBlocks.length === 1
        ? [createBlock()]
        : prevBlocks.filter((_, blockIndex) => blockIndex !== index)
    );
  };

  const upsertSequence = sequence => {
    if (!sequence?.id) return;
    setSequences(prevSequences => {
      const exists = prevSequences.some(item => String(item.id) === String(sequence.id));
      return exists
        ? prevSequences.map(item => (String(item.id) === String(sequence.id) ? sequence : item))
        : [...prevSequences, sequence];
    });
  };

  const openCreateSequence = (connectionId, applySelection) => {
    setSequenceModalId(null);
    setSequenceModalConnectionId(connectionId || "");
    sequenceModalApplySelection.current = applySelection;
    setSequenceModalOpen(true);
  };

  const openEditSequence = (sequenceId, connectionId, applySelection) => {
    if (!sequenceId || sequenceId === NO_SEQUENCE) return;
    setSequenceModalId(sequenceId);
    setSequenceModalConnectionId(connectionId || "");
    sequenceModalApplySelection.current = applySelection;
    setSequenceModalOpen(true);
  };

  const handleSequenceSaved = savedSequence => {
    const savedSequenceId = savedSequence?.id || sequenceModalId;
    upsertSequence(savedSequence);
    if (savedSequenceId && sequenceModalApplySelection.current) {
      sequenceModalApplySelection.current(String(savedSequenceId));
    }
    setSequenceModalOpen(false);
    setSequenceModalId(null);
    setSequenceModalConnectionId("");
    sequenceModalApplySelection.current = null;
  };

  const handleSequenceModalClose = () => {
    setSequenceModalOpen(false);
    setSequenceModalId(null);
    setSequenceModalConnectionId("");
    sequenceModalApplySelection.current = null;
  };

  const addVariableToBlock = (index, variable) => {
    setBlocks(prevBlocks =>
      prevBlocks.map((block, blockIndex) => {
        if (blockIndex !== index) return block;
        const separator = block.text?.trim() ? " " : "";
        return { ...block, text: `${block.text || ""}${separator}${variable}` };
      })
    );
  };

  const buildFormData = values => {
    const normalizedBlocks = blocks.map(block => ({
      text: block.text || "",
      delaySeconds: Number(block.delaySeconds || 0),
      media: block.file ? undefined : block.media,
    }));

    const isEditableQuickAnswer =
      values.actionType === "message" &&
      values.editBeforeSend &&
      blocks.length === 1 &&
      !blocks[0].file &&
      !blocks[0].media;

    const formData = new FormData();
    formData.append("shortcut", values.shortcut);
    formData.append("category", values.category || "General");
    formData.append("connectionId", values.connectionId || "");
    formData.append(
      "sharedConnectionIds",
      JSON.stringify((values.sharedConnectionIds || []).map(Number).filter(Boolean))
    );
    formData.append("actionType", values.actionType || "message");
    formData.append("chatbotSequenceId", values.chatbotSequenceId || "");
    formData.append(
      "message",
      normalizedBlocks
        .map(block => block.text)
        .filter(Boolean)
        .join("\n\n") || "Respuesta rápida con archivos"
    );
    formData.append("blocks", JSON.stringify(isEditableQuickAnswer ? [] : normalizedBlocks));

    blocks.forEach((block, index) => {
      if (block.file) {
        formData.append(`blockFile_${index}`, block.file);
      }
    });

    return formData;
  };

  const validateBlocks = () =>
    blocks.some(block => block.text.trim() || block.file || block.media);

  const handleSaveQuickAnswer = async values => {
    try {
      if (!values.sharedConnectionIds?.length) {
        toast.error("Selecciona al menos una conexion.");
        return;
      }
      if (values.actionType === "message" && !validateBlocks()) {
        toast.error("Agrega al menos un texto o archivo.");
        return;
      }
      if (values.actionType === "sequence" && !values.chatbotSequenceId) {
        toast.error("Selecciona una secuencia.");
        return;
      }
      if (values.editBeforeSend) {
        const canEditBeforeSend =
          values.actionType === "message" &&
          blocks.length === 1 &&
          blocks[0].text.trim() &&
          !blocks[0].file &&
          !blocks[0].media;

        if (!canEditBeforeSend) {
          toast.error("Editar antes de enviar solo funciona con un unico mensaje de texto sin archivos.");
          return;
        }
      }

      const formData = buildFormData(values);
      const config = { headers: { "Content-Type": "multipart/form-data" } };

      if (quickAnswerId) {
        await api.put(`/quickAnswers/${quickAnswerId}`, formData, config);
        handleClose();
      } else {
        const { data } = await api.post("/quickAnswers", formData, config);
        if (onSave) {
          onSave(data);
        }
        handleClose();
      }
      toast.success(i18n.t("quickAnswersModal.success"));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {quickAnswerId
            ? `${i18n.t("quickAnswersModal.title.edit")}`
            : `${i18n.t("quickAnswersModal.title.add")}`}
        </DialogTitle>
        <Formik
          initialValues={quickAnswer}
          enableReinitialize={true}
          validationSchema={QuickAnswerSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveQuickAnswer(values).finally(() => {
                actions.setSubmitting(false);
              });
            }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.formGrid}>
                  <div className={classes.textQuickAnswerContainer}>
                    <Field
                      as={TextField}
                      label={i18n.t("quickAnswersModal.form.shortcut")}
                      name="shortcut"
                      autoFocus
                      error={touched.shortcut && Boolean(errors.shortcut)}
                      helperText={touched.shortcut && errors.shortcut}
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                      fullWidth
                    />
                  </div>
                  <Autocomplete
                    freeSolo
                    options={categories}
                    value={values.category || "General"}
                    onChange={(event, value) =>
                      setFieldValue("category", value || "General")
                    }
                    onInputChange={(event, value) =>
                      setFieldValue("category", value || "General")
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={i18n.t("quickAnswersModal.form.category")}
                        variant="outlined"
                        margin="dense"
                      />
                    )}
                  />
                  <FormControl variant="outlined" className={classes.formControl}>
                    <InputLabel>Conexiones</InputLabel>
                    <Field
                      as={Select}
                      multiple
                      name="sharedConnectionIds"
                      label="Conexiones"
                      value={values.sharedConnectionIds || []}
                      onChange={event => {
                        const nextConnectionIds = event.target.value.map(Number);
                        const currentPrimary = Number(values.connectionId || 0);
                        const nextPrimaryConnectionId = nextConnectionIds.includes(currentPrimary)
                          ? currentPrimary
                          : nextConnectionIds[0] || "";

                        setFieldValue("sharedConnectionIds", nextConnectionIds);
                        setFieldValue("connectionId", nextPrimaryConnectionId);
                        if (String(nextPrimaryConnectionId) !== String(values.connectionId || "")) {
                          setFieldValue("chatbotSequenceId", "");
                        }
                      }}
                      renderValue={selected =>
                        selected.length
                          ? connections
                              .filter(whatsapp => selected.includes(whatsapp.id))
                              .map(whatsapp => whatsapp.name || whatsapp.number)
                              .join(", ")
                          : "Seleccionar conexiones"
                      }
                    >
                      {connections.map(whatsapp => (
                        <MenuItem key={whatsapp.id} value={whatsapp.id}>
                          <Checkbox
                            checked={(values.sharedConnectionIds || []).includes(whatsapp.id)}
                            color="primary"
                          />
                          {whatsapp.name || whatsapp.number || `Conexión ${whatsapp.id}`}
                        </MenuItem>
                      ))}
                    </Field>
                  </FormControl>
                  <FormControl variant="outlined" className={classes.formControl}>
                    <InputLabel>{i18n.t("quickAnswersModal.form.actionType")}</InputLabel>
                    <Field
                      as={Select}
                      name="actionType"
                      label={i18n.t("quickAnswersModal.form.actionType")}
                    >
                      <MenuItem value="message">
                        {i18n.t("ticketRemindersModal.actionTypes.message")}
                      </MenuItem>
                      <MenuItem value="sequence">
                        {i18n.t("ticketRemindersModal.actionTypes.sequence")}
                      </MenuItem>
                    </Field>
                  </FormControl>
                </div>

                {values.actionType !== "sequence" && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(values.editBeforeSend)}
                        onChange={event => {
                          const { checked } = event.target;
                          setFieldValue("editBeforeSend", checked);
                          if (checked) {
                            const firstText = blocks[0]?.text || "";
                            setBlocks([{ ...createBlock(), text: firstText }]);
                          }
                        }}
                        color="primary"
                      />
                    }
                    label="Editar antes de enviar"
                  />
                )}

                {values.actionType === "sequence" ? (
                  <>
                    <FormControl variant="outlined" className={classes.formControl}>
                      <InputLabel>{i18n.t("quickAnswersModal.form.sequence")}</InputLabel>
                      <Select
                        value={values.chatbotSequenceId || NO_SEQUENCE}
                        label={i18n.t("quickAnswersModal.form.sequence")}
                        MenuProps={sequenceSelectMenuProps}
                        onChange={event => {
                          if (event.target.value === "__create__") {
                            openCreateSequence(values.connectionId, sequenceId =>
                              setFieldValue("chatbotSequenceId", sequenceId)
                            );
                            return;
                          }
                          setFieldValue(
                            "chatbotSequenceId",
                            event.target.value === NO_SEQUENCE ? "" : event.target.value
                          );
                        }}
                      >
                        <MenuItem value={NO_SEQUENCE}>Sin secuencia</MenuItem>
                        <MenuItem value="__create__">Crear secuencia...</MenuItem>
                        {sequences
                          .filter(
                            sequence => {
                              if (
                                String(sequence.id) ===
                                String(values.chatbotSequenceId || "")
                              ) {
                                return true;
                              }

                              const selectedConnectionIds = normalizeConnectionIds(
                                values.sharedConnectionIds || [],
                                values.connectionId
                              );

                              return sequenceMatchesConnections(
                                sequence,
                                selectedConnectionIds
                              );
                            }
                          )
                          .map(sequence => (
                            <MenuItem key={sequence.id} value={String(sequence.id)}>
                              {formatSequenceOption(sequence)}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <div>
                      <Button
                        color="primary"
                        disabled={!values.chatbotSequenceId}
                        onClick={() =>
                          openEditSequence(
                            values.chatbotSequenceId,
                            values.connectionId,
                            sequenceId => setFieldValue("chatbotSequenceId", sequenceId)
                          )
                        }
                      >
                        Editar secuencia
                      </Button>
                    </div>
                  </>
                ) : blocks.map((block, index) => (
                  <Paper
                    className={classes.block}
                    elevation={0}
                    key={`quick-answer-block-${index}`}
                  >
                    <div className={classes.blockHeader}>
                      <Typography variant="subtitle2">
                        Mensaje {index + 1}
                      </Typography>
                      {!values.editBeforeSend && (
                      <div className={classes.blockActions}>
                        <IconButton
                          size="small"
                          onClick={() => moveBlock(index, -1)}
                          disabled={index === 0}
                        >
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveBlock(index, 1)}
                          disabled={index === blocks.length - 1}
                        >
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => removeBlock(index)}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </div>
                      )}
                    </div>

                    <div className={classes.blockGrid}>
                      <MessageBubblePreview block={block} />
                      <TextField
                        label="Mensaje"
                        value={block.text}
                        onChange={event =>
                          updateBlock(index, { text: event.target.value })
                        }
                        variant="outlined"
                        margin="dense"
                        multiline
                        minRows={5}
                        fullWidth
                        placeholder="Escribe el mensaje..."
                        className={values.editBeforeSend ? classes.messageTextWide : undefined}
                      />
                      {!values.editBeforeSend && (
                        <TextField
                          className={classes.messageDelayField}
                          label="Espera de"
                          type="number"
                          value={block.delaySeconds}
                          onChange={event =>
                            updateBlock(index, {
                              delaySeconds: Math.max(0, Number(event.target.value)),
                            })
                          }
                          variant="outlined"
                          margin="dense"
                          inputProps={{ min: 0, step: 1 }}
                          helperText="Segundos"
                          fullWidth
                        />
                      )}
                    </div>

                    <div className={classes.variablesBox}>
                      <Typography variant="caption" className={classes.variablesLabel}>
                        Variables disponibles
                      </Typography>
                      <div className={classes.variablesChips}>
                        {messageVariables.map(variable => (
                          <Chip
                            key={`quick-answer-${index}-${variable.value}`}
                            size="small"
                            label={variable.label}
                            onClick={() => addVariableToBlock(index, variable.value)}
                          />
                        ))}
                      </div>
                    </div>

                    {!values.editBeforeSend && (
                      <>
                    <div className={classes.fileRow}>
                      <input
                        accept={acceptedMedia}
                        id={`quick-answer-file-${index}`}
                        type="file"
                        style={{ display: "none" }}
                        onChange={event =>
                          updateBlock(index, {
                            file: event.target.files?.[0] || null,
                            media: event.target.files?.[0] ? null : block.media,
                          })
                        }
                      />
                      <label htmlFor={`quick-answer-file-${index}`}>
                        <Button variant="outlined" component="span" size="small">
                          Seleccionar archivo
                        </Button>
                      </label>
                      <Typography variant="body2" className={classes.fileName}>
                        {block.file?.name ||
                          block.media?.originalName ||
                          "Sin archivo"}
                      </Typography>
                      {(block.file || block.media) && (
                        <Button
                          size="small"
                          onClick={() =>
                            updateBlock(index, { file: null, media: null })
                          }
                        >
                          Quitar
                        </Button>
                      )}
                    </div>
                    <small className={classes.helper}>
                      Imagenes, stickers .webp, audios, videos y documentos.
                    </small>
                      </>
                    )}
                  </Paper>
                ))}

                {values.actionType !== "sequence" && !values.editBeforeSend && (
                  <Button
                    startIcon={<Add />}
                    color="primary"
                    onClick={() => setBlocks(prevBlocks => [...prevBlocks, createBlock()])}
                    style={{ marginTop: 12 }}
                  >
                    Agregar mensaje
                  </Button>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("quickAnswersModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {quickAnswerId
                    ? `${i18n.t("quickAnswersModal.buttons.okEdit")}`
                    : `${i18n.t("quickAnswersModal.buttons.okAdd")}`}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
      <ChatbotModal
        open={sequenceModalOpen}
        onClose={handleSequenceModalClose}
        onSave={handleSequenceSaved}
        sequenceId={sequenceModalId}
        defaultConnectionId={sequenceModalConnectionId}
        initialSequenceName="Secuencia de respuesta rapida"
      />
    </div>
  );
};

export default QuickAnswersModal;

